import { Calendar } from '@fullcalendar/core';
import { convertEventsObject } from '../calendar/conversion';
import interactionPlugin from '@fullcalendar/interaction';
import dayGridPlugin from '@fullcalendar/daygrid';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function replaceToolbarTitle() {
  const cal = window.djlCalendar?.fullCalendar;
  const viewDate = cal ? cal.getDate() : new Date();
  const year = viewDate.getFullYear();
  const monthIdx = viewDate.getMonth();

  const preloadSel = `.preloaded-months [data-month="${MONTHS[monthIdx]}"]`;
  const preloadEl = document.querySelector(preloadSel);
  if (!preloadEl) return;

  const realTitle = document.querySelector('.fc-toolbar-title');
  if (!realTitle) return;

  const newText = `${preloadEl.textContent} ${year}`;
  if (realTitle.textContent !== newText) {
    realTitle.setAttribute('translate', 'no');
    realTitle.textContent = newText;
  }
}

function fixDayNumbers() {
  const dayNumbers = document.querySelectorAll('.fc-daygrid-day-number');

  dayNumbers.forEach(el => {
    el.setAttribute('translate', 'no');

    let span = el.querySelector('[data-calendar-content]');
    if (!span) {
      const ariaLabel = el.getAttribute('aria-label') || '';
      const match = ariaLabel.match(/\b(\d{1,2})\b/);

      if (!match) return;
      span = document.createElement('span');
      span.dataset.calendarContent = 'true';
      span.setAttribute('translate', 'no');
      const [firstMatch] = match;
      span.textContent = firstMatch;
      const targetEl = el;
      targetEl.innerHTML = '';
      targetEl.appendChild(span);
    }

    [...el.childNodes].forEach(node => {
      if (node !== span) el.removeChild(node);
    });
  });
}

function setupCalendarTranslation() {
  const preloadContainer = document.createElement('div');
  preloadContainer.className = 'preloaded-months';
  preloadContainer.style.position = 'absolute';
  preloadContainer.style.height = '0';
  preloadContainer.style.overflow = 'hidden';
  preloadContainer.setAttribute('aria-hidden', 'true');
  preloadContainer.setAttribute('role', 'presentation');

  MONTHS.forEach(month => {
    const monthEl = document.createElement('span');
    monthEl.setAttribute('data-month', month);
    monthEl.textContent = `${month}`;
    preloadContainer.appendChild(monthEl);
  });

  document.body.appendChild(preloadContainer);

  const preloadObserver = new MutationObserver(muts => {
    muts.forEach(m => {
      if (
        m.type === 'characterData'
        || (m.type === 'attributes' && m.attributeName.startsWith('data-gt'))
      ) {
        replaceToolbarTitle();
      }
    });
  });

  preloadObserver.observe(preloadContainer, {
    subtree: true,
    characterData: true,
    attributes: true,
  });

  const calendarEl = document.getElementById('calendar');
  if (calendarEl) {
    const observer = new MutationObserver(() => {
      fixDayNumbers();
    });

    observer.observe(calendarEl, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }
}

const defaultOptions = {
  plugins: [dayGridPlugin, interactionPlugin],
  initialView: 'dayGridMonth',
  headerToolbar: {
    left: 'prev',
    center: 'title',
    right: 'next',
  },
  height: 'auto',
  selectable: true,
  navLinks: false,
  fixedWeekCount: 6,
  dateClick() {},
  eventClick() {},
  eventContent(arg) {
    const eventEl = document.createElement('span');
    eventEl.classList.add('fc-event-dot');
    eventEl.setAttribute('role', 'button');
    eventEl.setAttribute('tabindex', '0');

    let label = `Event: ${arg.event.title}`;
    if (arg.event.start) {
      const eventDate = arg.event.start.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
      label += ` on ${eventDate}`;
      const eventTime = arg.event.start.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
      label += ` at ${eventTime}`;
    }
    eventEl.setAttribute('aria-label', label);
    return { domNodes: [eventEl] };
  },
};

export default function djlCalendar(calendarEl, options) {
  const calendarOptions = { ...defaultOptions, ...options };
  return new Calendar(calendarEl, calendarOptions);
}

class DjlCalendar {
  constructor(calendarEl) {
    this.calendarEl = calendarEl;
    this.selectedDayEl = null;
    this.selectedDate = null;
    this.calendar = null;
    this._highlighting = false;
    this._initTimer = null;
    try {
      const serverDataEl = document.getElementById('serverData');
      const serverData = serverDataEl ? JSON.parse(serverDataEl.textContent) : {};
      this.currentAppointments = serverData.events || [];
      this.siteTimeZone = serverData.timeZone || null;
    } catch (_e) {
      this.currentAppointments = [];
      this.siteTimeZone = null;
    }
  }

  _clearSelectedDay() {
    this.calendar?.el
      .querySelector('td.fc-daygrid-day.fc-day-selected')
      ?.classList.remove('fc-day-selected');
  }

  highlightDay(dateStr) {
    if (this._highlighting || !this.calendar || !this.calendar.el || !dateStr) return;
    this._highlighting = true;
    try {
      this._clearSelectedDay();
      const dayEl = this.calendar.el.querySelector(`td.fc-daygrid-day[data-date="${dateStr}"]`);
      if (dayEl) {
        dayEl.classList.add('fc-day-selected');
        this.selectedDate = dateStr;
        this.selectedDayEl = dayEl;
        if (window.djlCalendar?.events?.focusByDate) {
          window.djlCalendar.events.focusByDate(dateStr);
        } else if (typeof window.djlCalendar?.focusByDateHandler === 'function') {
          window.djlCalendar.focusByDateHandler(dateStr);
        }
      } else {
        this.selectedDate = null;
        this.selectedDayEl = null;
      }
    } finally {
      this._highlighting = false;
    }
  }

  setHighlightOnly(dateStr) {
    if (!this.calendar || !this.calendar.el || !dateStr) return;
    this._clearSelectedDay();
    const dayEl = this.calendar.el.querySelector(`td.fc-daygrid-day[data-date="${dateStr}"]`);
    if (dayEl) {
      dayEl.classList.add('fc-day-selected');
      this.selectedDate = dateStr;
      this.selectedDayEl = dayEl;
    }
  }

  render() {
    const { currentAppointments, calendarEl } = this;

    const eventList = currentAppointments.map(occurrence => {
      if (!occurrence || !occurrence.start_at) {
        return null;
      }
      return {
        id: occurrence.id,
        title: occurrence.name,
        start: occurrence.start_at,
        end: occurrence.end_at,
        extendedProps: {
          model: occurrence,
        },
      };
    }).filter(Boolean);

    const calendar = djlCalendar(calendarEl, {
      events: eventList,
      eventContent: (arg) => {
        const eventEl = document.createElement('span');
        eventEl.classList.add('fc-event-dot');
        eventEl.setAttribute('role', 'button');
        eventEl.setAttribute('tabindex', '0');

        let label = `Event: ${arg.event.title}`;
        if (arg.event.start) {
          const dateOpts = { month: 'long', day: 'numeric' };
          const timeOpts = { hour: 'numeric', minute: '2-digit' };
          if (this.siteTimeZone) {
            dateOpts.timeZone = this.siteTimeZone;
            timeOpts.timeZone = this.siteTimeZone;
          }
          const eventDate = arg.event.start.toLocaleDateString(undefined, dateOpts);
          label += ` on ${eventDate}`;
          const eventTime = arg.event.start.toLocaleTimeString(undefined, timeOpts);
          label += ` at ${eventTime}`;
        }
        eventEl.setAttribute('aria-label', label);
        return { domNodes: [eventEl] };
      },
      dateClick: (info) => {
        this.highlightDay(info.dateStr);
      },
      eventDidMount: (info) => {
        const wrapper = info.el;
        if (wrapper?.tagName === 'A') {
          wrapper.removeAttribute('href');
          wrapper.setAttribute('tabindex', '-1');
          wrapper.setAttribute('role', 'presentation');
          wrapper.setAttribute('aria-hidden', 'true');
        }
        const dotEl = wrapper?.querySelector('.fc-event-dot') || wrapper;
        if (!dotEl) return;
        const keydownHandler = (e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          if (e.key === ' ') e.preventDefault();
          const dateStr = info.event.startStr?.substring(0, 10) ?? null;
          if (dateStr) this.setHighlightOnly(dateStr);
          if (window.djlCalendar?.events?.focusByEvent) {
            window.djlCalendar.events.focusByEvent(info.event);
          }
          if (typeof dotEl.focus === 'function') dotEl.focus();
          if (window.djlCalendar?.events?.openDetailsModalForCurrentEvent) {
            window.djlCalendar.events.openDetailsModalForCurrentEvent();
          }
        };
        dotEl._djlKeydownHandler = keydownHandler;
        dotEl.addEventListener('keydown', keydownHandler);
      },
      eventWillUnmount: (info) => {
        const wrapper = info.el;
        const dotEl = wrapper?.querySelector?.('.fc-event-dot') || wrapper;
        if (dotEl?._djlKeydownHandler) {
          dotEl.removeEventListener('keydown', dotEl._djlKeydownHandler);
          delete dotEl._djlKeydownHandler;
        }
      },
      eventClick: (info) => {
        const dateStr = info.event.startStr?.substring(0, 10) ?? null;
        if (dateStr) this.setHighlightOnly(dateStr);
        if (window.djlCalendar?.events?.focusByEvent) {
          window.djlCalendar.events.focusByEvent(info.event);
        }

        const target = info.jsEvent?.target;
        const dotEl = target?.closest?.('.fc-event-dot') ?? target;
        if (dotEl?.focus && document.body.contains(dotEl)) {
          dotEl.focus();
        }

        if (window.djlCalendar?.events?.openDetailsModalForCurrentEvent) {
          window.djlCalendar.events.openDetailsModalForCurrentEvent();
        }

        if (info.event.url) info.jsEvent.preventDefault();
      },
      datesSet: (info) => {
        replaceToolbarTitle();
        fixDayNumbers();

        // Patch aria-label on toolbar icon spans to fix axe role-img-alt violation
        calendarEl.querySelectorAll('.fc-toolbar button span[role="img"]').forEach((span) => {
          if (span.classList.contains('fc-icon-chevron-left')) {
            span.setAttribute('aria-label', 'Previous');
          } else if (span.classList.contains('fc-icon-chevron-right')) {
            span.setAttribute('aria-label', 'Next');
          }
        });

        let activeEventDate = null;

        if (window.djlCalendar?.events && this.currentAppointments.length > 0) {
          const currentIndex = window.djlCalendar.events.getCurrentIndex
            ? window.djlCalendar.events.getCurrentIndex() : 0;

          const currentEvent = this.currentAppointments[currentIndex];

          if (currentEvent && currentEvent.start_at) {
            activeEventDate = currentEvent.start_at.substring(0, 10);
          }
        }

        const dateToHighlight = activeEventDate || this.selectedDate;

        if (dateToHighlight) {
          const visibleStart = info.start;
          const visibleEnd = info.end;
          const dateToCheck = new Date(dateToHighlight);

          if (dateToCheck >= visibleStart && dateToCheck < visibleEnd) {
            this.setHighlightOnly(dateToHighlight);
          } else {
            this._clearSelectedDay();
            this.selectedDayEl = null;
            this.selectedDate = null;
          }
        }

        replaceToolbarTitle();
      },
    });

    this.calendar = calendar;
    if (!window.djlCalendar) window.djlCalendar = {};
    const djl = window.djlCalendar;
    djl.calendar = this;
    djl.fullCalendar = calendar;
    calendar.render();
    this.setupFilterListener();

    setupCalendarTranslation();

    clearTimeout(this._initTimer);
    this._initTimer = setTimeout(() => {
      if (window.djlCalendar?.events?.reload) {
        window.djlCalendar.events.reload(this.currentAppointments);
      } else {
        const firstOccurrence = this.currentAppointments[0];
        if (firstOccurrence?.start_at) {
          const initialDateStr = firstOccurrence.start_at.substring(0, 10);
          this.highlightDay(initialDateStr);
        }
      }
    }, 100);
  }

  resetSelectionForCurrentView(info) {
    const visibleStart = info.start;
    const visibleEnd = info.end;

    const eventsInView = this.calendar.getEvents().filter(event => {
      const eventStart = event.start;
      return eventStart >= visibleStart && eventStart < visibleEnd;
    });

    eventsInView.sort((a, b) => a.start.getTime() - b.start.getTime());

    if (eventsInView.length > 0) {
      const firstVisibleEvent = eventsInView[0];
      const dateStr = firstVisibleEvent.startStr.substring(0, 10);

      setTimeout(() => {
        this.highlightDay(dateStr);

        if (window.djlCalendar?.events && typeof window.djlCalendar.events.reload === 'function') {
          const eventsOnDate = this.currentAppointments.filter(
            occ => occ.start_at && occ.start_at.startsWith(dateStr),
          );

          if (eventsOnDate.length > 0) {
            window.djlCalendar.events.reload(this.currentAppointments);
            window.djlCalendar.events.focusByDate(dateStr);
          }
        }
      }, 50);
    } else {
      this.selectedDate = null;
      this.selectedDayEl = null;

      if (window.djlCalendar?.events?.reload) {
        window.djlCalendar.events.reload(this.currentAppointments);

        const midPoint = new Date((visibleStart.getTime() + visibleEnd.getTime()) / 2);
        const midPointStr = midPoint.toISOString().substring(0, 10);
        window.djlCalendar.events.focusByDate(midPointStr);
      }
    }
  }

  refreshCalendarEvents(appointments) {
    const { calendar } = this;
    if (!calendar) return [];

    const results = [];

    calendar.removeAllEvents();

    const formattedForVue = appointments.map(event => {
      if (!event) return null;
      const occurrences = event.occurrences_attributes || event.occurrences || [];
      return occurrences.map(occ => ({
        ...occ,
        event_id: event.id,
        name: event.name,
        description: event.description,
      }));
    }).flat().filter(Boolean);

    formattedForVue.sort((a, b) => {
      const aDate = a.start_at || '';
      const bDate = b.start_at || '';
      return aDate.localeCompare(bDate);
    });

    appointments.forEach((appt) => {
      const eventData = appt.event || appt;
      const eventList = convertEventsObject(eventData);
      eventList.forEach((e) => {
        results.push(e);
        calendar.addEvent(e);
      });
    });

    this.currentAppointments = formattedForVue;
    if (window.djlCalendar?.events?.reload) {
      window.djlCalendar.events.reload(formattedForVue);
    }

    return results;
  }

  fetchAndRefreshEvents() {
    const apptFilterSelect = document.getElementById('calendar_appt_inclusion_filter');

    let filterValue = '';
    if (apptFilterSelect) {
      filterValue = apptFilterSelect.value;
    }

    const url = apptFilterSelect?.getAttribute('data-refresh-url') || '/calendar_events';
    const urlWithFilter = `${url}?inclusion=${encodeURIComponent(filterValue)}`;

    const $iconLoading = $(apptFilterSelect).parents('.calendar-inclusion-filter').find('.icon-loading');
    $iconLoading?.addClass('animate-spin');

    $.ajax({
      url: urlWithFilter,
      cache: false,
      dataType: 'json',
      method: 'GET',
    }).done((data) => {
      this.refreshCalendarEvents(data);
    }).fail(() => {
      this.refreshCalendarEvents([]);
    }).always(() => {
      $iconLoading?.removeClass('animate-spin');
    });
  }

  setupFilterListener() {
    const apptFilterSelect = document.getElementById('calendar_appt_inclusion_filter');

    if (!apptFilterSelect) return;

    const $apptFilterSelect = $(apptFilterSelect);
    $apptFilterSelect.off('change.calendarLoad').on('change.calendarLoad', () => {
      this.fetchAndRefreshEvents();
    });

    if (!$apptFilterSelect.val()) {
      this.fetchAndRefreshEvents();
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const calendarEl = document.getElementById('calendar');
  if (calendarEl) {
    const calendarInstance = new DjlCalendar(calendarEl);
    calendarInstance.render();
  }
});

global.DjlCalendar = DjlCalendar;
